import { useEffect, useMemo, useState } from "react";
import { cachedGet } from "../lib/sharedData";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtMoney, fmtDate, daysBetween, formatMarketValue } from "../lib/format";
import { Plus, Upload, Users, UserCircle, UserX } from "lucide-react";
import BulkImportClients from "../components/BulkImportClients";
import TierBadge from "../components/TierBadge";
import { useT } from "../lib/i18n";

export default function ClientList() {
  const t = useT();
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [me, setMe] = useState(null);
  const [search] = useSearchParams();
  const [q, setQ] = useState(search.get("q") || "");
  const [scope, setScope] = useState(localStorage.getItem("pm_client_scope") || "all"); // all | mine | unassigned
  const [tierFilter, setTierFilter] = useState(localStorage.getItem("pm_client_tier") || "all"); // all | client | network | mandated
  const [importOpen, setImportOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const refresh = () => {
    api.get("/clients").then((r) => setClients(r.data || []));
    api.get("/import/history").then((r) => setHistory((r.data || []).filter((h) => h.kind === "clients"))).catch(() => {});
  };
  useEffect(() => {
    refresh();
    cachedGet("/auth/me", 5 * 60000).then((d) => ({ data: d })).then((r) => {
      setMe(r.data);
      const wsid = localStorage.getItem("pm_active_workspace_id") || r.data?.primary_workspace_id;
      if (wsid) api.get(`/workspaces/${wsid}/members`).then((mr) => setMembers(mr.data?.members || [])).catch(() => {});
    }).catch(() => {});
  }, []);

  const memberById = useMemo(() => {
    const m = {};
    for (const x of members) m[x.agent_id] = x;
    return m;
  }, [members]);

  const setScopeAndStore = (v) => { setScope(v); try { localStorage.setItem("pm_client_scope", v); } catch (e) { /* noop */ } };
  const setTierAndStore = (v) => { setTierFilter(v); try { localStorage.setItem("pm_client_tier", v); } catch (e) { /* noop */ } };

  const tierCounts = useMemo(() => ({
    all:      clients.length,
    client:   clients.filter((c) => (c.tier || "client") === "client").length,
    network:  clients.filter((c) => c.tier === "network").length,
    mandated: clients.filter((c) => c.tier === "mandated").length,
  }), [clients]);

  const scopeCounts = useMemo(() => {
    const mineId = me?.id;
    return {
      all: clients.length,
      mine: clients.filter((c) => c.assigned_agent_id === mineId).length,
      unassigned: clients.filter((c) => !c.assigned_agent_id).length,
    };
  }, [clients, me?.id]);

  const filtered = useMemo(() => {
    let arr = clients;
    if (tierFilter !== "all") arr = arr.filter((c) => (c.tier || "client") === tierFilter);
    if (scope === "mine") arr = arr.filter((c) => c.assigned_agent_id === me?.id);
    if (scope === "unassigned") arr = arr.filter((c) => !c.assigned_agent_id);
    if (!q) return arr;
    const s = q.toLowerCase();
    return arr.filter((c) => [c.full_name, c.current_club, c.position, c.nationality].some((v) => (v || "").toLowerCase().includes(s)));
  }, [clients, q, scope, tierFilter, me?.id]);

  const TIER_TABS = [
    { id: "all",      label: t("All"),      testid: "tier-tab-all" },
    { id: "client",   label: t("Clients"),  testid: "tier-tab-client" },
    { id: "network",  label: t("Network"),  testid: "tier-tab-network" },
    { id: "mandated", label: t("Mandated"), testid: "tier-tab-mandated" },
  ];

  const SCOPE_TABS = [
    { id: "all",        label: t("Everyone"),   Icon: Users,       testid: "scope-all" },
    { id: "mine",       label: t("Mine"),       Icon: UserCircle,  testid: "scope-mine" },
    { id: "unassigned", label: t("Unassigned"), Icon: UserX,       testid: "scope-unassigned" },
  ];

  return (
    <Layout
      title="Client roster"
      subtitle="Players under representation"
      right={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="pm-btn-secondary h-10 w-10 px-0 sm:w-auto sm:px-4 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
            data-testid="import-clients-btn"
            aria-label="Import clients"
          >
            <Upload size={14} /> <span className="hidden sm:inline">Import clients</span>
          </button>
          <Link
            to="/clients/new"
            className="pm-btn-primary h-10 w-10 px-0 sm:w-auto sm:px-4 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
            data-testid="add-client-btn"
            aria-label="New client"
          >
            <Plus size={14} /> <span className="hidden sm:inline">New client</span>
          </Link>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="client-tier-tabs">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mr-1 w-full sm:w-auto" data-testid="client-tier-caption">{t("Player type")}</span>
        {TIER_TABS.map((tb) => {
          const active = tierFilter === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setTierAndStore(tb.id)}
              data-testid={tb.testid}
              className={`h-9 px-3.5 rounded-full border text-[12px] font-bold flex items-center gap-1.5 transition-all ${
                active
                  ? "bg-emerald-600 text-white border-emerald-700 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
              }`}
            >
              {tb.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono-tab ${active ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>
                {tierCounts[tb.id]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="client-scope-tabs">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mr-1 w-full sm:w-auto" data-testid="client-scope-caption">{t("Assigned agent")}</span>
        {SCOPE_TABS.map((tb) => {
          const Icon = tb.Icon;
          const active = scope === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setScopeAndStore(tb.id)}
              data-testid={tb.testid}
              className={`h-9 px-3 rounded-full border text-[12px] font-bold flex items-center gap-1.5 transition-all ${
                active
                  ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
              }`}
            >
              <Icon size={12} />
              {tb.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono-tab ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>
                {scopeCounts[tb.id]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-6">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by player, club, position…"
          className="pm-input max-w-md"
          data-testid="clients-search-input"
        />
        <div className="text-xs text-slate-500 font-mono-tab">{t("{a} of {b} players", { a: filtered.length, b: clients.length })}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((c) => {
          const daysToEnd = daysBetween(new Date().toISOString(), c.contract_end);
          const expiryTone = daysToEnd != null && daysToEnd <= 180 ? "pm-pill-red" : daysToEnd != null && daysToEnd <= 365 ? "pm-pill-amber" : "pm-pill-green";
          return (
            <Link
              key={c.id}
              to={`/clients/${c.id}`}
              className="pm-card pm-card-hover p-5 flex gap-4 group"
              data-testid={`client-card-${c.id}`}
            >
              <div className="w-20 h-24 rounded-xl overflow-hidden bg-slate-200 shrink-0 ring-1 ring-slate-200">
                <img src={c.photo_url} alt={c.full_name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display font-black text-lg tracking-tight truncate text-slate-900 group-hover:text-emerald-700 transition-colors">
                    {c.full_name}
                  </div>
                  <span className="text-base">{c.nationality_flag}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <TierBadge tier={c.tier || "client"} />
                  <div className="text-sm text-slate-500 truncate">{c.position} · {c.current_club}</div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Market value</div>
                    <div className="font-mono-tab font-bold text-slate-900" data-testid={`client-card-value-${c.id}`}>{formatMarketValue(c.market_value_eur)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Weekly wage</div>
                    <div className="font-mono-tab font-bold text-slate-900">{fmtMoney(c.weekly_wage, c.currency)}</div>
                  </div>
                </div>

                {(c.tier || "client") === "client" ? (
                  <>
                    <div className="mt-3 flex items-center justify-between">
                      <span className={`pm-pill ${expiryTone}`}>Expires {fmtDate(c.contract_end)}</span>
                      <span className="text-xs text-slate-400 font-mono-tab">{daysToEnd}d</span>
                    </div>
                  </>
                ) : (
                  <div className="mt-3 flex items-center justify-between">
                    <span className="pm-pill pm-pill-slate">
                      {c.tier === "network" ? "Available for introduction" : "Mandated · partner agency"}
                    </span>
                  </div>
                )}

                <div className="mt-2 flex items-center gap-1.5 text-[11px]" data-testid={`assigned-agent-${c.id}`}>
                  {c.assigned_agent_id ? (
                    <>
                      <span className="text-slate-400">Assigned to</span>
                      <span className={`font-bold truncate ${c.assigned_agent_id === me?.id ? "text-emerald-700" : "text-slate-700"}`}>
                        {memberById[c.assigned_agent_id]?.agent_name || "Teammate"}
                        {c.assigned_agent_id === me?.id ? " (you)" : ""}
                      </span>
                    </>
                  ) : (
                    <span className="text-amber-700 font-bold">Unassigned</span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
        {filtered.length === 0 && <div className="text-sm text-slate-500">No clients match your search.</div>}
      </div>

      {/* Import history (Phase 1 audit trail) */}
      {history.length > 0 && (
        <div className="mt-6" data-testid="client-import-history-section">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="text-[11px] font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-2"
            data-testid="toggle-import-history"
          >
            {showHistory ? t("Hide") : t("Show")} {t("import history")} · {history.length} {history.length === 1 ? t("record") : t("records")}
          </button>
          {showHistory && (
            <div className="pm-card divide-y divide-slate-100 mt-2">
              {history.slice(0, 10).map((h) => (
                <div key={h.id} className="p-4 flex items-center gap-4 text-sm" data-testid={`client-import-history-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{h.filename}</div>
                    <div className="text-[11px] text-slate-500">{new Date(h.created_at).toLocaleString()} · by {h.imported_by}</div>
                  </div>
                  <div className="text-xs text-slate-700 font-mono-tab">
                    <b>{h.created}</b> created · {h.skipped} skipped · {h.errors_count} errors
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <BulkImportClients
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => refresh()}
      />
    </Layout>
  );
}
